import { useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StarIcon } from "@/components/ui/StarIcon";
import { toIsoDate, useMonthMatrix } from "@/hooks/useMonthMatrix";
import { cn } from "@/lib/utils";
import type { EarningsCalendarItemOut } from "@/api/client";

const MONTH_GRID_DEFAULT_CLASS = "flex flex-col";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function MonthGrid({
  year,
  month,
  events,
  selectedId,
  onPrev,
  onNext,
  onSelectEvent,
  className,
}: {
  year: number;
  month: number;
  events: EarningsCalendarItemOut[];
  selectedId: number | null;
  onPrev: () => void;
  onNext: () => void;
  onSelectEvent: (item: EarningsCalendarItemOut) => void;
  className?: string;
}) {
  const { weeks } = useMonthMatrix(year, month);

  const eventsByDate = useMemo(() => {
    const map = new Map<string, EarningsCalendarItemOut[]>();
    for (const ev of events) {
      const list = map.get(ev.announcement_date) ?? [];
      list.push(ev);
      map.set(ev.announcement_date, list);
    }
    return map;
  }, [events]);

  const todayIso = toIsoDate(new Date());

  return (
    <div className={cn(MONTH_GRID_DEFAULT_CLASS, className)}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-border">
        <Button variant="outline" size="icon" className="h-7 w-7" onClick={onPrev} aria-label="Previous month">
          <ChevronLeft className="h-3.5 w-3.5" />
        </Button>
        <div className="font-semibold text-[13px] tabular-nums">
          {MONTH_NAMES[month - 1]} {year}
        </div>
        <Button variant="outline" size="icon" className="h-7 w-7" onClick={onNext} aria-label="Next month">
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="grid grid-cols-7 border-b border-border bg-muted/20">
        {WEEKDAY_LABELS.map((d) => (
          <div
            key={d}
            className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider text-muted-foreground text-center"
          >
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {weeks.flatMap((row) =>
          row.map((d) => {
            const iso = toIsoDate(d);
            const inMonth = d.getMonth() === month - 1;
            const dayEvents = eventsByDate.get(iso) ?? [];
            const isToday = iso === todayIso;
            const dayIsSelected =
              selectedId !== null && dayEvents.some((ev) => ev.id === selectedId);
            return (
              <div
                key={iso}
                className={cn(
                  "min-h-[80px] border-r border-b border-border/60 p-1.5 flex flex-col gap-1 transition-colors",
                  !inMonth && "bg-muted/10 text-muted-foreground/60",
                  isToday && !dayIsSelected && "bg-primary/10",
                  dayIsSelected && "bg-accent ring-1 ring-inset ring-primary",
                )}
              >
                <div className="flex items-center justify-between text-[11px] font-mono tabular-nums">
                  <span className={cn(isToday && "font-semibold text-foreground")}>
                    {d.getDate()}
                  </span>
                </div>
                <div className="flex flex-col gap-0.5">
                  {dayEvents.slice(0, 3).map((ev) => (
                    <button
                      key={ev.id}
                      onClick={() => onSelectEvent(ev)}
                      aria-pressed={ev.id === selectedId}
                      className={cn(
                        "text-left px-1 py-0.5 rounded-sm text-[10px] font-mono truncate hover:brightness-125 transition",
                        ev.id === selectedId
                          ? "bg-primary text-primary-foreground"
                          : "bg-[hsl(var(--chart-up))]/15 text-foreground hover:bg-[hsl(var(--chart-up))]/30",
                      )}
                      title={`${ev.primary_symbol ?? ""} — ${ev.company_name}`}
                    >
                      <span className="inline-flex items-center gap-0.5 min-w-0 max-w-full">
                        {ev.starred && <StarIcon className="h-2.5 w-2.5" />}
                        <span className="truncate">{ev.primary_symbol ?? ev.company_name}</span>
                      </span>
                    </button>
                  ))}
                  {dayEvents.length > 3 && (
                    <div className="text-[9px] text-muted-foreground">
                      +{dayEvents.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          }),
        )}
      </div>
    </div>
  );
}
